import { useState } from "react";
import { updateProfile, submitPlacement, type CefrLevel, type UserRow } from "../lib/api";

type Step = "welcome" | "placement" | "result";

interface Props {
  teacherName: string;
  onDone: (user: UserRow) => void;
}

interface PlacementQ {
  level: CefrLevel;
  question: string;
  hint: string;
}

const PLACEMENT: PlacementQ[] = [
  {
    level: "A1",
    question: "What is your name and where are you from?",
    hint: "A short sentence is fine — just say it naturally.",
  },
  {
    level: "A2",
    question: "Tell me about your last weekend. What did you do?",
    hint: "Try to use past-tense verbs.",
  },
  {
    level: "B1",
    question: "What do you do for work or study, and what do you like about it?",
    hint: "A few sentences — try to explain why.",
  },
  {
    level: "B2",
    question:
      "Describe a difficult decision you had to make recently. How did you decide?",
    hint: "Use any complex language you're comfortable with.",
  },
];

const GOAL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "conversation", label: "Conversational fluency" },
  { value: "travel", label: "Travel" },
  { value: "work", label: "Work / business" },
  { value: "exam", label: "Exam preparation" },
];

const LEVEL_BLURB: Record<CefrLevel, string> = {
  A1: "Beginner — you can use simple phrases for basic needs.",
  A2: "Elementary — you handle everyday situations and short exchanges.",
  B1: "Intermediate — you discuss familiar topics and make plans.",
  B2: "Upper-intermediate — you can hold most conversations with ease.",
  C1: "Advanced — you speak fluently and use language flexibly.",
  C2: "Mastery — you express yourself naturally on any topic.",
};

export function Onboarding({ teacherName, onDone }: Props): JSX.Element {
  const [step, setStep] = useState<Step>("welcome");
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("conversation");
  const [answers, setAnswers] = useState<string[]>(PLACEMENT.map(() => ""));
  const [qIdx, setQIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ level: CefrLevel; user: UserRow } | null>(null);

  async function startPlacement(): Promise<void> {
    if (!name.trim()) {
      setError("Please tell me your name.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await updateProfile({ name: name.trim(), goal });
      setStep("placement");
      setQIdx(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function answerCurrent(text: string): void {
    setAnswers((prev) => {
      const next = [...prev];
      next[qIdx] = text;
      return next;
    });
  }

  async function nextQuestion(): Promise<void> {
    if (!answers[qIdx]?.trim()) {
      setError("Type at least a short answer to continue.");
      return;
    }
    setError(null);
    if (qIdx < PLACEMENT.length - 1) {
      setQIdx(qIdx + 1);
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitPlacement(
        PLACEMENT.map((q, i) => ({ question: q.question, answer: answers[i] })),
      );
      setResult({ level: result.level, user: result.user });
      setStep("result");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function skipPlacement(): void {
    setSubmitting(true);
    submitPlacement([{ question: "Skipped", answer: "Hello, my name is " + name + "." }])
      .then((r) => {
        setResult({ level: r.level, user: r.user });
        setStep("result");
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 p-4 overflow-y-auto">
      <div className="w-full max-w-xl rounded-2xl border border-ink-700 bg-ink-900/95 shadow-2xl">
        <div className="px-6 pt-6 pb-2 border-b border-ink-700">
          <div className="text-[10px] uppercase tracking-widest text-ink-500">
            {step === "welcome" ? "Step 1 of 3" : step === "placement" ? `Step 2 of 3 · question ${qIdx + 1}/${PLACEMENT.length}` : "Step 3 of 3"}
          </div>
          <h1 className="text-xl font-semibold text-white mt-1">
            {step === "welcome"
              ? `Welcome — I'm ${teacherName}`
              : step === "placement"
                ? "Quick placement"
                : "You're all set"}
          </h1>
        </div>

        {step === "welcome" && (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-ink-300 leading-relaxed">
              I'll be your English teacher. First, let me get to know you — then a short
              placement check so I can pitch our conversations at the right level.
            </p>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-400">Your name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Marek"
                className="mt-1 w-full rounded-lg bg-ink-800 border border-ink-600 px-3 py-2 text-white placeholder:text-ink-500 focus:border-lime-500/60 focus:outline-none"
                autoFocus
              />
            </label>

            <div>
              <span className="text-xs uppercase tracking-widest text-ink-400">Your main goal</span>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {GOAL_OPTIONS.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGoal(g.value)}
                    className={`rounded-lg border px-3 py-2 text-sm text-left transition ${
                      goal === g.value
                        ? "border-lime-500 bg-lime-500/15 text-white"
                        : "border-ink-600 bg-ink-800/60 text-ink-300 hover:border-ink-500"
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-700 bg-red-900/40 px-3 py-2 text-sm text-red-200">
                {error}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                disabled={submitting}
                onClick={() => void startPlacement()}
                className="rounded-lg bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-ink-900 font-medium px-4 py-2 text-sm"
              >
                {submitting ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === "placement" && (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-ink-300 leading-relaxed">
              Just type a natural answer in English. Don't worry about mistakes — that's exactly
              what I'm here to help with. Skip if you'd rather start chatting right away.
            </p>

            <div className="rounded-xl border border-ink-700 bg-ink-800/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-1">
                Question {qIdx + 1} of {PLACEMENT.length}
              </div>
              <div className="text-white font-medium">{PLACEMENT[qIdx].question}</div>
              <div className="text-xs text-ink-400 mt-1">{PLACEMENT[qIdx].hint}</div>
            </div>

            <textarea
              value={answers[qIdx] ?? ""}
              onChange={(e) => answerCurrent(e.target.value)}
              placeholder="Type your answer in English…"
              rows={4}
              className="w-full rounded-lg bg-ink-800 border border-ink-600 px-3 py-2 text-white placeholder:text-ink-500 focus:border-lime-500/60 focus:outline-none resize-none"
              autoFocus
            />

            {error && (
              <div className="rounded-lg border border-red-700 bg-red-900/40 px-3 py-2 text-sm text-red-200">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                disabled={submitting}
                onClick={skipPlacement}
                className="text-xs text-ink-400 hover:text-white underline"
              >
                Skip placement, I'll figure it out later
              </button>
              <button
                disabled={submitting}
                onClick={() => void nextQuestion()}
                className="rounded-lg bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-ink-900 font-medium px-4 py-2 text-sm"
              >
                {submitting
                  ? "Scoring…"
                  : qIdx === PLACEMENT.length - 1
                    ? "Finish placement"
                    : "Next question"}
              </button>
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl border border-lime-500/40 bg-lime-500/10 p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-lime-300 mb-1">
                Your starting level
              </div>
              <div className="text-4xl font-semibold text-white">{result.level}</div>
              <div className="text-xs text-ink-300 mt-2">{LEVEL_BLURB[result.level]}</div>
            </div>

            <p className="text-sm text-ink-300 leading-relaxed">
              I'll adapt my vocabulary and pace to your level. We'll bump it up as you get more
              comfortable — and you can always check your progress in the dashboard.
            </p>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => onDone(result.user)}
                className="rounded-lg bg-lime-500 hover:bg-lime-400 text-ink-900 font-medium px-4 py-2 text-sm"
              >
                Start my first lesson
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
